import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyBookings } from '@/api/hooks/useBookings';
import type { BookingStatus } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatLocalDate, formatLocalTime } from '@/lib/format';
import { useVenueContext } from '@/providers/VenueProvider';

type TabFilter = 'upcoming' | 'past' | 'cancelled';

const STATUS_COLORS: Record<BookingStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  ACTIVE: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-gray-100 text-gray-600',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

const STATUS_BORDER: Record<BookingStatus, string> = {
  PENDING: 'border-l-yellow-500',
  CONFIRMED: 'border-l-green-500',
  ACTIVE: 'border-l-primary',
  COMPLETED: 'border-l-gray-300',
  CANCELLED: 'border-l-red-500',
  NO_SHOW: 'border-l-orange-500',
  EXPIRED: 'border-l-gray-300',
};

export default function MyBookingsPage() {
  const { t, i18n } = useTranslation();
  const { currentSlug } = useVenueContext();
  const [tab, setTab] = useState<TabFilter>('upcoming');
  const [page, setPage] = useState(0);

  const queryParams = {
    upcoming: tab === 'cancelled' ? undefined : tab === 'upcoming',
    status: tab === 'cancelled' ? 'CANCELLED' : undefined,
    page,
    size: 10,
  };

  const { data, isLoading, isError } = useMyBookings(queryParams);

  const tabs: { key: TabFilter; label: string }[] = [
    { key: 'upcoming', label: t('booking.tab_upcoming') },
    { key: 'past', label: t('booking.tab_past') },
    { key: 'cancelled', label: t('booking.tab_cancelled') },
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      <h1 className="text-2xl font-semibold">{t('booking.my_bookings')}</h1>

      <div className="flex gap-2">
        {tabs.map((t) => (
          <Button
            key={t.key}
            variant={tab === t.key ? 'default' : 'outline'}
            size="sm"
            className="min-w-[90px] text-center whitespace-nowrap"
            onClick={() => {
              setTab(t.key);
              setPage(0);
            }}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="flex gap-4 p-4">
                <Skeleton className="h-16 w-16 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {t('common.error')}
        </div>
      )}

      {data && data.bookings.length === 0 && (
        <div className="py-12 text-center">
          <p className="mb-4 text-muted-foreground">{t('booking.no_bookings')}</p>
          <Link
            to={`/venue/${currentSlug}/book`}
            className={buttonVariants({ variant: 'default' })}
          >
            {t('common.book_now')}
          </Link>
        </div>
      )}

      {data && data.bookings.length > 0 && (
        <div className="space-y-3">
          {data.bookings.map((booking) => (
            <Link
              key={booking.id}
              to={booking.status === 'ACTIVE' ? `/session/${booking.id}` : `/bookings/${booking.id}`}
              className="block"
            >
              <Card className={`transition-colors hover:bg-accent/50 border-l-4 ${STATUS_BORDER[booking.status]}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</span>
                        <Badge
                          variant="secondary"
                          className={`${STATUS_COLORS[booking.status]} min-w-[80px] text-center`}
                        >
                          {booking.status === 'ACTIVE'
                            ? t('session.session_in_progress')
                            : t(`booking.status_${booking.status.toLowerCase()}`)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {booking.unitName} &middot; {booking.locationName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatLocalDate(booking.startTimeLocal, i18n.language)} &middot;{' '}
                        {formatLocalTime(booking.startTimeLocal, i18n.language)} –{' '}
                        {formatLocalTime(booking.endTimeLocal, i18n.language)}
                      </p>
                      {booking.addonSummary && (
                        <p className="text-xs text-muted-foreground">
                          {booking.addonSummary}
                        </p>
                      )}
                    </div>
                    <span className="font-mono text-sm font-medium">
                      {formatCurrency(booking.totalPrice, booking.currency, i18n.language)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}

          {data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.back')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {page + 1} / {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('booking.tab_next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
